import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { api_url } from "../../../../../config/config";
import tools from "../../../../../helpers/tools";
import HasRoles from "../../../../common/Can/HasRoles";

const ContractSearchHitech = ({ contractData, catalog, onAttachContract }) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [attachedContractId, setAttachedContractId] = useState(
    contractData?.contract?.IdHitech || null,
  );
  const [searchNumeroDossier, setSearchNumeroDossier] = useState("");
  const [searchImmat, setSearchImmat] = useState("");

  // Synchroniser attachedContractId avec contractData.contract.IdHitech
  useEffect(() => {
    setAttachedContractId(contractData?.contract?.IdHitech || null);
  }, [contractData?.contract?.IdHitech]);

  const handleAttachContract = (contract) => {
    setAttachedContractId(contract.cle_dossier);
    onAttachContract(contract.cle_dossier);
    toast.success(
      `Contrat "${contract.numero_dossier}" (${contract.immatriculation}) rattaché avec succès`,
    );
  };

  const handleSearchByNumeroDossier = async () => {
    if (!searchNumeroDossier.trim()) {
      toast.warning("Veuillez renseigner le numéro de dossier");
      return;
    }

    setIsSearching(true);
    setHasSearched(false);

    try {
      const params = new URLSearchParams();
      params.append("numeroDossier", searchNumeroDossier);

      const response = await axios.get(
        `${api_url}contracts/hitech/${catalog}/chercher-correspondances?${params.toString()}`,
      );

      setSearchResults(response.data);
      setHasSearched(true);

      if (response.data && response.data.length > 0) {
        toast.success(`${response.data.length} correspondance(s) trouvée(s)`);
      } else {
        toast.info("Aucune correspondance trouvée");
      }
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
      toast.error("Erreur lors de la recherche de correspondances");
      setSearchResults(null);
      setHasSearched(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchByImmat = async () => {
    if (!searchImmat.trim()) {
      toast.warning("Veuillez renseigner l'immatriculation");
      return;
    }

    setIsSearching(true);
    setHasSearched(false);

    try {
      const params = new URLSearchParams();
      params.append("immat", searchImmat);

      const response = await axios.get(
        `${api_url}contracts/hitech/${catalog}/chercher-correspondances?${params.toString()}`,
      );

      setSearchResults(response.data);
      setHasSearched(true);

      if (response.data && response.data.length > 0) {
        toast.success(`${response.data.length} correspondance(s) trouvée(s)`);
      } else {
        toast.info("Aucune correspondance trouvée");
      }
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
      toast.error("Erreur lors de la recherche de correspondances");
      setSearchResults(null);
      setHasSearched(true);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <HasRoles roles={["SUPER_ADMIN"]}>
      <div className="contract-search-hitech mb-4">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fa fa-link mr-2" />
              Rattachement à un contrat HITECH existant
            </h5>
          </div>
          <div className="card-body">
            <div className="alert alert-warning mb-3">
              <h6 className="alert-heading mb-2">
                <i className="fa fa-exclamation-triangle mr-2" />
                Fonctionnalité réservée aux super-administrateurs
              </h6>
              <p className="mb-2">
                Si le contrat a déjà été créé directement sur HITECH (sans
                passer par MATCH), vous pouvez le rechercher ci-dessous pour
                rattacher MATCH à ce contrat existant.
              </p>
              <p className="mb-0">
                <strong>Important :</strong> Vous devrez quand même remplir tous
                les champs du formulaire ci-dessous pour que MATCH puisse
                enregistrer les informations du contrat dans sa base de données.
              </p>
            </div>
            <div className="alert alert-info mb-3">
              <i className="fa fa-info-circle mr-2" />
              <strong>Recherche disponible par :</strong> numéro de dossier OU
              immatriculation.
              <br />
              <small>
                Si vous ne trouvez pas le contrat, cela signifie qu'il n'existe
                pas encore sur HITECH et qu'il sera créé automatiquement lors de
                la génération.
              </small>
            </div>

            <div className="row mb-3">
              {/* Colonne 1 : Recherche par numéro de dossier */}
              <div className="col-md-6">
                <div className="border rounded p-3 h-100">
                  <h6 className="font-weight-bold mb-3">
                    <i className="fa fa-folder mr-2" />
                    Par numéro de dossier
                  </h6>
                  <div className="mb-3">
                    <label className="font-weight-bold">
                      Numéro de dossier
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={searchNumeroDossier}
                      onChange={(e) => setSearchNumeroDossier(e.target.value)}
                      placeholder="Ex : 21100005"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") handleSearchByNumeroDossier();
                      }}
                    />
                  </div>
                  <button
                    className="btn btn-primary btn-block"
                    onClick={handleSearchByNumeroDossier}
                    disabled={isSearching}
                  >
                    {isSearching ? (
                      <>
                        <i className="fa fa-spinner fa-spin mr-2" />
                        Recherche...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-search mr-2" />
                        Rechercher par numéro
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Séparateur OU */}
              <div className="col-md-12 d-md-none text-center my-2">
                <span className="badge badge-secondary">OU</span>
              </div>
              <div
                className="d-none d-md-flex align-items-center justify-content-center"
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 1,
                }}
              >
                <span className="badge badge-secondary">OU</span>
              </div>

              {/* Colonne 2 : Recherche par immatriculation */}
              <div className="col-md-6">
                <div className="border rounded p-3 h-100">
                  <h6 className="font-weight-bold mb-3">
                    <i className="fa fa-id-card mr-2" />
                    Par immatriculation
                  </h6>
                  <div className="mb-3">
                    <label className="font-weight-bold">Immatriculation</label>
                    <input
                      type="text"
                      className="form-control"
                      value={searchImmat}
                      onChange={(e) => setSearchImmat(e.target.value)}
                      placeholder="Ex : FE128EW"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") handleSearchByImmat();
                      }}
                    />
                  </div>
                  <button
                    className="btn btn-primary btn-block"
                    onClick={handleSearchByImmat}
                    disabled={isSearching}
                  >
                    {isSearching ? (
                      <>
                        <i className="fa fa-spinner fa-spin mr-2" />
                        Recherche...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-search mr-2" />
                        Rechercher par immatriculation
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {hasSearched && searchResults !== null && (
              <div className="mt-3">
                {searchResults.length === 0 ? (
                  <div className="alert alert-secondary">
                    <i className="fa fa-info-circle mr-2" />
                    Aucun contrat trouvé sur HITECH. Un nouveau contrat sera
                    créé automatiquement lors de la génération avec les
                    informations du formulaire ci-dessous.
                  </div>
                ) : (
                  <div className="alert alert-success">
                    <h6 className="alert-heading">
                      <i className="fa fa-check-circle mr-2" />
                      {searchResults.length} correspondance(s) trouvée(s)
                    </h6>
                    <div className="table-responsive mt-3">
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th>N° Dossier</th>
                            <th>Immatriculation</th>
                            <th>Client</th>
                            <th>Véhicule</th>
                            <th>Date début</th>
                            <th>Date fin prévue</th>
                            <th>Durée (mois)</th>
                            <th style={{ width: "150px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {searchResults.map((contract, index) => (
                            <tr key={index}>
                              <td>{contract.numero_dossier || "-"}</td>
                              <td>
                                <strong>
                                  {contract.immatriculation || "-"}
                                </strong>
                              </td>
                              <td>
                                {contract.intitule_client || "-"}
                                <br />
                                <small className="text-muted">
                                  {contract.code_client || "-"}
                                </small>
                              </td>
                              <td>{contract.marque_type || "-"}</td>
                              <td>
                                {contract.date_debut
                                  ? tools.formatDate(contract.date_debut)
                                  : "-"}
                              </td>
                              <td>
                                {contract.date_fin_prevue
                                  ? tools.formatDate(contract.date_fin_prevue)
                                  : "-"}
                              </td>
                              <td>{contract.duree_totale_prevue || "-"}</td>
                              <td>
                                {attachedContractId === contract.cle_dossier ? (
                                  <span className="badge badge-success">
                                    <i className="fa fa-check mr-1" />
                                    Rattaché
                                  </span>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() =>
                                      handleAttachContract(contract)
                                    }
                                    title="Rattacher MATCH à ce contrat HITECH existant"
                                  >
                                    <i className="fa fa-link mr-1" />
                                    Rattacher
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </HasRoles>
  );
};

export default ContractSearchHitech;
